import { ChevronRight, Ellipsis, FilePlus, Pencil } from 'lucide-react'
import { Link, useNavigate } from 'react-router'

import { Money } from '@/components/ds'
import { InitialsAvatar } from '@/components/ParticipantAvatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { Participant } from '@/interfaces/Participant'
import { cn } from '@/lib/utils'

import { Balance, currencyEntries } from './contactUtils'
import { VatPayerReliability } from './VatPayerReliability'

interface ContactItemProps {
  contact: Participant
  balance?: Balance
  /** true = zůstatky se ještě načítají */
  balanceLoading?: boolean
  primaryCurrency: string
  canEdit?: boolean
}

export const ContactItem = ({ contact, balance, balanceLoading, primaryCurrency, canEdit }: ContactItemProps) => {
  const navigate = useNavigate()
  const open = balance ? currencyEntries(balance.open, primaryCurrency) : []
  const hasOverdue = !!balance?.overdueCount

  return (
    <TableRow onClick={() => navigate(`/contact/${contact.id}`)} className="group h-11 cursor-pointer">
      <TableCell className="max-w-0">
        <div className="flex min-w-0 items-center gap-3">
          <InitialsAvatar name={contact.name} className="size-7 text-[11px]" />
          <div className="min-w-0 md:contents">
            <div className="flex min-w-0 items-center gap-3 md:contents">
              <Link
                to={`/contact/${contact.id}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate font-medium hover:underline"
              >
                {contact.name}
              </Link>
              {contact.vatPayer === 'vatPayer' && contact.vatPayerReliability === 'notReliable' && (
                <VatPayerReliability participant={contact} />
              )}
            </div>
            {(contact.ico || contact.address?.city) && (
              <p className="truncate text-xs text-muted-foreground md:hidden">
                {contact.ico ? `IČO ${contact.ico}` : contact.address?.city}
              </p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden tabular text-muted-foreground md:table-cell">{contact.ico || '-'}</TableCell>
      <TableCell className="hidden max-w-0 truncate text-muted-foreground md:table-cell">{contact.email || '-'}</TableCell>
      <TableCell className="hidden max-w-0 truncate text-muted-foreground md:table-cell">{contact.address?.city || '-'}</TableCell>
      <TableCell className="text-right">
        {balanceLoading ? (
          <Skeleton className="ml-auto h-4 w-20" />
        ) : open.length ? (
          <div className="flex flex-col items-end">
            {open.map(([currency, amount]) => (
              <Money key={currency} amount={amount} currency={currency} className={cn(hasOverdue && 'text-danger')} />
            ))}
            {hasOverdue && <span className="text-xs text-danger">po splatnosti</span>}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell className="w-10 pr-2 pl-1 text-right md:w-20 md:pl-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Akce"
                  className="size-7 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 md:data-[state=open]:opacity-100"
                >
                  <Ellipsis />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to={`/invoice/new?customerId=${contact.id}`}>
                    <FilePlus />
                    Vystavit fakturu
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={`/order/new?customerId=${contact.id}`}>
                    <FilePlus />
                    Vystavit výzvu k platbě
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to={`/contact/${contact.id}/edit`}>
                    <Pencil />
                    Upravit
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <ChevronRight className="hidden size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block" />
        </div>
      </TableCell>
    </TableRow>
  )
}
